import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarCheck,
  CheckCircle2,
  Circle,
  ArrowRight,
  Dumbbell,
  RotateCcw,
  Bookmark,
  Timer,
  CalendarDays,
  GraduationCap,
} from "lucide-react";
import { toast } from "sonner";
import StudyShell from "@/components/study/StudyShell";
import { createStudyPlan, getStudyPlan, togglePlanDay } from "@/lib/api";
import { STUDY } from "@/constants/testIds";

const PLAN_OPTIONS = [
  { type: "7-day", label: "7-Day Plan", blurb: "Focused one-week sprint to exam day." },
  { type: "14-day", label: "14-Day Plan", blurb: "Two weeks of steady, balanced prep." },
  { type: "30-day", label: "30-Day Plan", blurb: "Thorough month-long mastery track." },
  { type: "48-hour", label: "Final 48-Hour Cram", blurb: "High-yield review for the last two days." },
];

const ACTION_META = {
  learn: { to: "/study/learn", cta: "Open lesson", icon: GraduationCap },
  practice: { to: "/study/practice", cta: "Practice", icon: Dumbbell },
  missed: { to: "/study/missed", cta: "Redo missed", icon: RotateCcw },
  bookmarks: { to: "/study/bookmarks", cta: "Bookmarks", icon: Bookmark },
  sprint: { to: "/study/mini-exams", cta: "Open exam mode", icon: Timer },
};

const fmtDay = (iso) => {
  if (!iso) return "";
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    return iso;
  }
};

export function PlanTasks({ tasks }) {
  return (
    <ul className="space-y-2">
      {(tasks || []).map((t, idx) => {
        const meta = t.action ? ACTION_META[t.action.kind] : null;
        const Icon = meta?.icon;
        return (
          <li
            key={idx}
            className="flex items-start justify-between gap-3 rounded-xl bg-[var(--ibp-offwhite)] px-3.5 py-2.5"
          >
            <span className="flex items-start gap-2 text-sm leading-6 text-[var(--ibp-ink)]">
              {Icon ? <Icon className="mt-0.5 h-4 w-4 flex-none text-[var(--ibp-navy-950)]" /> : null}
              {t.label}
            </span>
            {meta ? (
              <Link
                to={meta.to}
                className="inline-flex flex-none items-center gap-1 text-xs font-semibold text-[var(--ibp-navy-950)] hover:underline"
              >
                {meta.cta} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function CreatePlan({ onCreated }) {
  const [type, setType] = useState("14-day");
  const [examDate, setExamDate] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      await createStudyPlan(type, examDate);
      toast.success("Study plan ready");
      onCreated();
    } catch (e) {
      toast.error("Could not create plan", { description: e?.response?.data?.detail || "" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-6 shadow-[0_10px_30px_rgba(7,20,39,0.05)]">
      <h2 className="text-base font-semibold text-[var(--ibp-ink)]">Build your study plan</h2>
      <p className="mt-1 text-sm text-[color:rgba(11,18,32,0.65)]">
        Pick a plan length. We&apos;ll tailor each day to your weak topics and missed questions.
      </p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {PLAN_OPTIONS.map((o) => {
          const active = type === o.type;
          return (
            <button
              key={o.type}
              type="button"
              data-testid={STUDY.planTypeOption}
              onClick={() => setType(o.type)}
              className={[
                "rounded-xl border p-4 text-left transition-colors",
                active
                  ? "border-[var(--ibp-gold-500)] bg-[color:rgba(214,162,30,0.1)]"
                  : "border-[color:rgba(10,27,51,0.15)] bg-white hover:border-[var(--ibp-navy-950)]",
              ].join(" ")}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-[var(--ibp-ink)]">{o.label}</span>
                {active ? <CheckCircle2 className="h-4 w-4 text-[var(--ibp-gold-500)]" /> : null}
              </div>
              <p className="mt-1 text-xs text-[color:rgba(11,18,32,0.6)]">{o.blurb}</p>
            </button>
          );
        })}
      </div>

      <label className="mt-4 flex flex-col gap-1 text-xs font-medium text-[color:rgba(11,18,32,0.6)]">
        Exam date (optional — anchors your plan to test day)
        <input
          type="date"
          data-testid={STUDY.planExamDate}
          value={examDate}
          onChange={(e) => setExamDate(e.target.value)}
          className="h-10 w-full max-w-xs rounded-lg border border-[color:rgba(10,27,51,0.2)] bg-white px-3 text-sm text-[var(--ibp-ink)] outline-none focus:border-[var(--ibp-navy-950)]"
        />
      </label>

      <button
        type="button"
        data-testid={STUDY.planCreate}
        onClick={create}
        disabled={busy}
        className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[var(--ibp-gold-500)] px-6 py-3 text-sm font-semibold text-[var(--ibp-ink)] shadow-[0_14px_30px_rgba(214,162,30,0.2)] transition-colors hover:bg-[var(--ibp-primary-hover)] disabled:opacity-60"
      >
        <CalendarCheck className="h-4 w-4" /> {busy ? "Building..." : "Create my plan"}
      </button>
    </div>
  );
}

function PlanView({ plan, onChanged, onChangePlan }) {
  const qc = useQueryClient();
  const toggle = async (day, done) => {
    try {
      await togglePlanDay(day, done);
      qc.invalidateQueries({ queryKey: ["study-plan"] });
      onChanged();
    } catch (e) {
      toast.error("Could not update day");
    }
  };

  return (
    <div>
      <div className="rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-6 shadow-[0_10px_30px_rgba(7,20,39,0.05)]">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-bold text-[var(--ibp-ink)]">
              <CalendarCheck className="h-5 w-5 text-[var(--ibp-gold-500)]" /> {plan.label}
            </h2>
            <p className="mt-1 text-sm text-[color:rgba(11,18,32,0.65)]">
              {plan.exam_date ? (
                <span className="inline-flex items-center gap-1">
                  <CalendarDays className="h-4 w-4" /> Exam {fmtDay(plan.exam_date)}
                  {typeof plan.days_until_exam === "number" && plan.days_until_exam >= 0
                    ? ` — ${plan.days_until_exam} day(s) to go`
                    : ""}
                </span>
              ) : (
                "No exam date set"
              )}
            </p>
          </div>
          <button
            type="button"
            data-testid={STUDY.planChange}
            onClick={onChangePlan}
            className="rounded-lg border border-[color:rgba(10,27,51,0.2)] px-4 py-2 text-sm font-semibold text-[var(--ibp-ink)] hover:bg-[color:rgba(10,27,51,0.05)]"
          >
            Change plan
          </button>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between text-xs font-semibold text-[color:rgba(11,18,32,0.6)]">
            <span>
              {plan.progress.completed} of {plan.progress.total} days done
            </span>
            <span>{plan.progress.pct}%</span>
          </div>
          <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-[color:rgba(10,27,51,0.08)]">
            <div
              className="h-full rounded-full bg-[var(--ibp-gold-500)] transition-all"
              style={{ width: `${plan.progress.pct}%` }}
            />
          </div>
        </div>
      </div>

      <div className="mt-5 space-y-4">
        {plan.days.map((d) => {
          const isToday = d.day === plan.today_day;
          return (
            <div
              key={d.day}
              data-testid={STUDY.planDay}
              className={[
                "rounded-2xl border bg-white p-5 shadow-[0_10px_30px_rgba(7,20,39,0.04)]",
                isToday
                  ? "border-[var(--ibp-gold-500)] ring-1 ring-[var(--ibp-gold-500)]"
                  : "border-[color:rgba(10,27,51,0.12)]",
              ].join(" ")}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold uppercase tracking-wide text-[color:rgba(11,18,32,0.5)]">
                      Day {d.day}
                      {d.date ? ` · ${fmtDay(d.date)}` : ""}
                    </span>
                    {isToday ? (
                      <span className="rounded-full bg-[var(--ibp-gold-500)] px-2 py-0.5 text-[10px] font-bold uppercase text-[var(--ibp-ink)]">
                        Today
                      </span>
                    ) : null}
                  </div>
                  <h3 className="mt-1 text-base font-semibold text-[var(--ibp-ink)]">{d.title}</h3>
                </div>
                <button
                  type="button"
                  data-testid={STUDY.planDayToggle}
                  onClick={() => toggle(d.day, !d.done)}
                  className={[
                    "inline-flex flex-none items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors",
                    d.done
                      ? "bg-[color:rgba(21,128,61,0.12)] text-[#15803d]"
                      : "border border-[color:rgba(10,27,51,0.2)] text-[var(--ibp-ink)] hover:bg-[color:rgba(10,27,51,0.05)]",
                  ].join(" ")}
                >
                  {d.done ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
                  {d.done ? "Done" : "Mark done"}
                </button>
              </div>
              <div className="mt-3">
                <PlanTasks tasks={d.tasks} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function StudyPlan() {
  const qc = useQueryClient();
  const [forceCreate, setForceCreate] = useState(false);
  const { data: plan, isLoading } = useQuery({
    queryKey: ["study-plan"],
    queryFn: getStudyPlan,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["study-plan"] });

  return (
    <StudyShell testId={STUDY.planPage}>
      <h1 className="text-2xl font-bold text-[var(--ibp-ink)]">Study plan</h1>
      <p className="mt-1 text-sm text-[color:rgba(11,18,32,0.6)]">
        A day-by-day path to exam day, tailored to your progress.
      </p>

      <div className="mt-6">
        {isLoading ? (
          <p className="text-sm text-[color:rgba(11,18,32,0.6)]">Loading...</p>
        ) : !plan || forceCreate ? (
          <CreatePlan
            onCreated={() => {
              setForceCreate(false);
              refresh();
            }}
          />
        ) : (
          <PlanView plan={plan} onChanged={refresh} onChangePlan={() => setForceCreate(true)} />
        )}
      </div>
    </StudyShell>
  );
}
